import { useState } from 'react';
import { HardHat, LayoutDashboard, Lock } from 'lucide-react';
import { PageHeader } from '@/components/ui';
import { cn } from '@/lib/utils';
import { canDashboard, type DashboardKind } from '@/lib/permissions';
import { useAuthStore } from '@/store/authStore';
import { DashboardOperationnelPage } from './DashboardOperationnelPage';
import { DashboardPatronPage } from './DashboardPatronPage';

const STORAGE_KEY = 'aw_dashboard_vue';

/**
 * Page d'accueil : affiche le tableau de bord auquel l'utilisateur a droit
 * (permissions « voir_dashboard_patron » / « voir_dashboard_operationnel »).
 * S'il a droit aux deux, un sélecteur permet de basculer entre Direction et Terrain.
 */
export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const patron = canDashboard(user, 'patron');
  const op = canDashboard(user, 'operationnel');
  const both = patron && op;

  const [vue, setVue] = useState<DashboardKind>(() => {
    const saved = localStorage.getItem(STORAGE_KEY) as DashboardKind | null;
    if (saved === 'operationnel' && op) return 'operationnel';
    if (saved === 'patron' && patron) return 'patron';
    return patron ? 'patron' : 'operationnel';
  });
  const choisir = (k: DashboardKind) => { setVue(k); localStorage.setItem(STORAGE_KEY, k); };

  // Tableau réellement affiché selon les droits et le choix.
  const actif: DashboardKind | null = patron && (vue === 'patron' || !op) ? 'patron' : op ? 'operationnel' : null;

  return (
    <>
      <PageHeader
        title={actif === 'operationnel' ? 'Tableau de bord — Opérationnel' : actif === 'patron' ? 'Tableau de bord — Direction' : 'Tableau de bord'}
        subtitle={actif === 'operationnel' ? 'Vue terrain — Advanced Works, Oran' : "Vue d'ensemble — Advanced Works, Oran"}
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'Tableau de bord' }]}
        actions={both ? (
          <div className="inline-flex rounded-md border border-border p-0.5">
            {([['patron', 'Direction', LayoutDashboard], ['operationnel', 'Terrain', HardHat]] as const).map(([k, label, Icon]) => (
              <button key={k} onClick={() => choisir(k)}
                className={cn('inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-sm font-medium transition-colors',
                  (actif === k) ? 'bg-brand-accent text-brand-accent-contrast' : 'text-content-secondary hover:bg-surface-hover')}>
                <Icon className="size-4" /> {label}
              </button>
            ))}
          </div>
        ) : undefined}
      />

      {actif === 'patron' && <DashboardPatronPage />}
      {actif === 'operationnel' && <DashboardOperationnelPage />}
      {!actif && (
        <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-border bg-surface-secondary py-20 text-center shadow-sm">
          <span className="flex size-14 items-center justify-center rounded-full bg-surface-primary text-content-muted"><Lock className="size-7" /></span>
          <p className="text-lg font-semibold text-content-primary">Aucun tableau de bord ne vous est attribué</p>
          <p className="max-w-md text-sm text-content-secondary">Demandez à l'administrateur de vous accorder l'accès au tableau de bord <strong>Direction</strong> et/ou <strong>Opérationnel</strong> depuis Paramètres → Utilisateurs.</p>
        </div>
      )}
    </>
  );
}
